import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircleIcon, ClockIcon, XCircleIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { makeUpdateMilestone } from "../../pages/Api";

export default function MilestoneTracker({ initialMilestones, invoiceId }) {
    const [milestones, setMilestones] = useState(initialMilestones);

    // Define status options with values and labels
    const statusOptions = [
        { value: "Pending", label: "Pending" },
        { value: "in_progress", label: "In Progress" },
        { value: "completed", label: "Completed" }
    ];

    const statusStyles = {
        completed: "bg-green-600/90 text-white",
        in_progress: "bg-yellow-500/90 text-white",
        Pending: "bg-gray-500/80 text-white",
    };

    const statusIcons = {
        completed: <CheckCircleIcon className="w-5 h-5 mr-2" />,
        in_progress: <ClockIcon className="w-5 h-5 mr-2" />,
        Pending: <XCircleIcon className="w-5 h-5 mr-2" />,
    };

    const handleStatusChange = async (index, newStatus) => {
        const milestoneId = milestones[index].id;
        
        try {
            await makeUpdateMilestone(milestoneId, newStatus, invoiceId);

            const updatedMilestones = milestones.map((milestone, i) =>
                i === index ? { ...milestone, status: newStatus } : milestone
            );
            setMilestones(updatedMilestones);
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <div className="space-y-6">
            {milestones?.map((milestone, index) => (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
                    className="bg-white p-6 rounded-lg shadow-lg flex items-center justify-between hover:shadow-xl transition-all border border-gray-200"
                >
                    <span className="font-semibold text-gray-900 text-lg w-1/3">{milestone.name}</span>

                    <div className="relative w-1/3 h-3 bg-gray-200 rounded-full overflow-hidden group">
                        <motion.div
                            animate={{ width: `${milestone.percentage}%` }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all"
                        >
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                            </div>
                        </motion.div>
                        <span className="opacity-0 group-hover:opacity-100 absolute top-[-26px] left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs font-semibold py-1 px-2 rounded-md shadow-lg transition-opacity">
                            {milestone.percentage}%
                        </span>
                    </div>

                    <span className="text-gray-900 font-semibold w-12 text-center">{milestone.percentage}%</span>

                    <div className="relative">
                        <div className="group relative">
                            <button
                                className={`flex items-center px-4 py-2 rounded-full text-sm font-semibold transition-all ${statusStyles[milestone.status]} shadow-md`}
                            >
                                {statusIcons[milestone.status]}
                                {
                                    statusOptions.find(option => option.value === milestone.status)?.label || milestone.status
                                }
                                <ChevronDownIcon className="ml-2 w-4 h-4" />
                            </button>
                            <div className="absolute left-0 top-full mt-2 bg-white rounded-md shadow-lg w-40 opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all">
                                {statusOptions.map(({ value, label }) => (
                                    <button
                                        key={value}
                                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition"
                                        onClick={() => handleStatusChange(index, value)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
}
